import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Menu, X, Moon, Sun } from "lucide-react";
import { Monogram } from "@/components/Monogram";

const LINKS = [
  { id: "about", label: "About" },
  { id: "beliefs", label: "Beliefs" },
  { id: "appointment", label: "Prayer" },
  { id: "gallery", label: "Media" },
  { id: "books", label: "Books" },
  { id: "community", label: "Community" },
  { id: "events", label: "Events" },
  { id: "blog", label: "Journal" },
  { id: "give", label: "Donate" },
  { id: "testimonies", label: "Testimonies" },
  { id: "contact", label: "Contact" },
];

export const Navbar = ({ scrollToId, solid = false }) => {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isSolid = solid || scrolled;

  useEffect(() => setMounted(true), []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || solid
          ? "backdrop-blur-md bg-parchment/80 dark:bg-ink/80 border-b border-border py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <button
          data-testid="nav-logo-home"
          onClick={() => go("home")}
          className="flex items-center gap-3 group"
        >
          <Monogram size={38} />
          <span className={`font-serif text-lg sm:text-xl font-semibold tracking-tight ${isSolid ? "text-foreground" : "text-parchment"}`}>
            Charles McKuhn
          </span>
        </button>

        <div className="hidden md:flex items-center gap-6">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => go(l.id)}
              className={`font-sans text-sm font-medium transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-gold hover:after:w-full after:transition-all after:duration-300 ${isSolid ? "text-foreground/80 hover:text-gold-deep dark:hover:text-gold-light" : "text-parchment/85 hover:text-gold-light"}`}
            >
              {l.label}
            </button>
          ))}
          {mounted && (
            <button
              data-testid="theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label="Toggle theme"
              className={`p-2 rounded-full border transition-colors ${isSolid ? "border-border text-foreground hover:border-gold" : "border-parchment/30 text-parchment hover:border-gold"}`}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          )}
          <button
            data-testid="nav-cta-booking"
            onClick={() => go("contact")}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${isSolid ? "bg-ink text-parchment dark:bg-gold dark:text-ink hover:bg-gold hover:text-ink dark:hover:bg-gold-light" : "bg-gold text-ink hover:bg-gold-light"}`}
          >
            Book an Engagement
          </button>
        </div>

        <div className="md:hidden flex items-center gap-2">
          {mounted && (
            <button
              data-testid="theme-toggle-mobile"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label="Toggle theme"
              className={`p-2 rounded-full border ${isSolid ? "border-border text-foreground" : "border-parchment/30 text-parchment"}`}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          )}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            className={`p-2 rounded-full border ${isSolid ? "border-border text-foreground" : "border-parchment/30 text-parchment"}`}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden mt-3 mx-4 rounded-2xl border border-border bg-card p-4 flex flex-col gap-1 shadow-xl">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-${l.id}-link`}
              onClick={() => go(l.id)}
              className="text-left px-3 py-2.5 rounded-lg font-medium hover:bg-secondary transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-cta-booking"
            onClick={() => go("contact")}
            className="mt-2 px-3 py-2.5 rounded-lg bg-ink text-parchment dark:bg-gold dark:text-ink font-semibold"
          >
            Book an Engagement
          </button>
        </div>
      )}
    </header>
  );
};
